/**
 * Copy/paste for annotations.
 *
 * Both ends work on the NORMALISED records (0..1) that are the source of truth,
 * never on the fabric objects. Reading geometry back off a fabric object needs
 * its full transform matrix and pathOffset — doing it by hand is what made a
 * pasted polygon render in one place and save in another.
 */

/** How far a pasted copy is nudged off the original, in screen px. */
export const PASTE_OFFSET_PX = 12;

/**
 * Largest shift in [-min, 1-max] — i.e. move by `delta`, but never so far that
 * the shape leaves the image. Returns 0 for a shape already larger than the
 * image, where no shift can satisfy both edges.
 */
const clampShift = (delta, min, max) => {
  const lower = -min;
  const upper = 1 - max;
  if (upper < lower) return 0;
  return Math.min(upper, Math.max(lower, delta));
};

/**
 * Snapshot the selected object's stored record, or null when the object is not
 * an annotation (or its record has gone).
 */
export const readClipboardEntry = (object, image) => {
  const kind = object?.data?.type;

  if (kind === 'bbox') {
    const ann = image?.annotations?.[object.data.annIndex];
    return ann ? { type: 'bbox', ann: { ...ann } } : null;
  }

  if (kind === 'polygon') {
    const poly = image?.polygons?.[object.data.polyIndex];
    if (!poly || !Array.isArray(poly.points) || poly.points.length < 3) return null;
    return {
      type: 'polygon',
      poly: { ...poly, points: poly.points.map((p) => ({ ...p })) },
    };
  }

  return null;
};

/** Offset a box, keeping it inside the image. Pasting counts as accepting an AI box. */
export const offsetAnnotation = (ann, deltaX, deltaY) => {
  const halfWidth = (ann.width || 0) / 2;
  const halfHeight = (ann.height || 0) / 2;

  return {
    ...ann,
    centerX: ann.centerX + clampShift(deltaX, ann.centerX - halfWidth, ann.centerX + halfWidth),
    centerY: ann.centerY + clampShift(deltaY, ann.centerY - halfHeight, ann.centerY + halfHeight),
    isAI: false,
  };
};

/** Offset every point of a polygon by one shift, keeping the shape inside the image. */
export const offsetPolygon = (poly, deltaX, deltaY) => {
  const xs = poly.points.map((p) => p.x);
  const ys = poly.points.map((p) => p.y);

  const shiftX = clampShift(deltaX, Math.min(...xs), Math.max(...xs));
  const shiftY = clampShift(deltaY, Math.min(...ys), Math.max(...ys));

  return {
    ...poly,
    points: poly.points.map((p) => ({ ...p, x: p.x + shiftX, y: p.y + shiftY })),
  };
};
